import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, PlayArrow as PlayIcon, Pause as PauseIcon, Stop as StopIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { deleteTask, updateTask, fetchTasks, finishAndCloneTask } from '../store/taskSlice';
import type { Task } from '../api/api';
import LoadingSpinner from './LoadingSpinner';

const formatDateTime = (isoString: string) => {
  const date = new Date(isoString);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const calculateDuration = (startDate: string, endDate: string | null) => {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  const diffMs = end.getTime() - start.getTime();

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours === 0 && minutes === 0) {
    const seconds = Math.floor(diffMs / 1000);
    return `${seconds}s`;
  }

  if (hours === 0) {
    return `${minutes}m`;
  }

  return `${hours}h ${minutes}m`;
};

const TaskListView: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const tasks = useAppSelector(state => state.tasks.tasks);
  const loading = useAppSelector(state => state.tasks.loading);
  const [taskToComplete, setTaskToComplete] = useState<number | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<number | null>(null);
  const [playingMap, setPlayingMap] = useState<Record<number, boolean>>({});

  useEffect(() => {
    dispatch(fetchTasks());
  }, [dispatch]);

  // initialize playing state for active tasks
  useEffect(() => {
    const map: Record<number, boolean> = {};
    tasks.forEach((t: Task) => {
      // if task is paused (isPaused === 'T') it's not playing
      map[t.id] = t.isPaused !== 'T';
    });
    setPlayingMap(map);
  }, [tasks]);

  const handleDelete = (id: number) => {
    setTaskToDelete(id);
  };

  const handleConfirmDelete = async () => {
    if (taskToDelete !== null) {
      try {
        await dispatch(deleteTask(taskToDelete)).unwrap();
        setTaskToDelete(null);
      } catch (error) {
        // Error deleting task
      }
    }
  };

  const handleCancelDelete = () => {
    setTaskToDelete(null);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleCompleteTask = (id: number) => {
    setTaskToComplete(id);
  };

  const handleConfirmComplete = async () => {
    if (taskToComplete !== null) {
      const taskToUpdate = tasks.find((t: Task) => t.id === taskToComplete);
      if (taskToUpdate) {
        try {
          // Get current local datetime in format "2025-11-02T14:30:00"
          const now = new Date();
          const localDateTime = new Date(now.getTime() - (now.getTimezoneOffset() * 60000))
            .toISOString()
            .slice(0, 19); // Remove 'Z' suffix, keep only datetime

          await dispatch(updateTask({
            ...taskToUpdate,
            endDate: localDateTime,
            isPaused: null
          })).unwrap();
        } catch (error) {
          // Error completing task
        }
      }
      setTaskToComplete(null);
    }
  };

  const handleCancelComplete = () => {
    setTaskToComplete(null);
  };

  // Filter active tasks: either endDate is null (running) OR explicitly paused ('T')
  const activeTasks = tasks
    .filter((task: Task) => task.endDate === null || task.isPaused === 'T')
    .sort((a, b) => {
      if (!a.startDate || !b.startDate) return 0;
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ px: { xs: 0, sm: 3 } }}>
        <Box sx={{ my: { xs: 0, sm: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <LoadingSpinner />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ my: { xs: 0, sm: 4 } }}>
        <Typography variant="h4" component="h1" sx={{ textAlign: 'center', pt: { xs: 1, sm: 0 }, mb: { xs: 1, sm: 2 }, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Active Tasks
        </Typography>
        {activeTasks.length === 0 ? (
          <Paper sx={{ p: { xs: 1, sm: 3 }, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No active tasks. Add a task to get started!
            </Typography>
          </Paper>
        ) : (
          <List>
            {activeTasks.map((task: Task) => (
              <Paper key={task.id} sx={{ mb: { xs: 1, sm: 2 } }}>
                <ListItem
                  sx={{
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    gap: { xs: 1, sm: 0 }
                  }}
                  secondaryAction={
                    <>
                      {(task.endDate === null || task.isPaused === 'T') && (
                        <>
                          <Tooltip title="Play / Pause" placement="top">
                            <IconButton
                              onClick={async () => {
                                // Toggle pause state and persist to backend
                                const now = new Date();
                                const localDateTime = new Date(now.getTime() - (now.getTimezoneOffset() * 60000))
                                  .toISOString()
                                  .slice(0, 19);

                                const currentlyPaused = task.isPaused === 'T';
                                try {
                                  if (currentlyPaused) {
                                    // If task is paused and user pressed play: finish paused task and create a clone that's active
                                    await dispatch(finishAndCloneTask(task.id)).unwrap();
                                  } else {
                                    // Pause: set isPaused -> 'T', set endDate to now
                                    await dispatch(updateTask({
                                      ...task,
                                      isPaused: 'T',
                                      endDate: localDateTime
                                    })).unwrap();
                                  }
                                } catch (err) {
                                  // ignore for now
                                }
                              }}
                              edge="end"
                              aria-label="play-pause"
                              size="medium"
                              sx={{ mr: 1 }}
                            >
                              {playingMap[task.id] ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Stop (complete task)" placement="top">
                            <IconButton
                              onClick={() => handleCompleteTask(task.id)}
                              edge="end"
                              aria-label="stop"
                              size="medium"
                              sx={{ mr: 1 }}
                            >
                              <StopIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Click to edit this task" placement="top">
                        <IconButton
                          onClick={() => handleNavigate(`/edit-task/${task.id}`)}
                          edge="end"
                          aria-label="edit"
                          size="medium"
                          sx={{ mr: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Click to delete this task" placement="top">
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDelete(task.id)}
                          color="error"
                          size="medium"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography variant="h6">{task.name}</Typography>
                    }
                    secondary={
                      <Box>
                        <Typography component="span" display="block">
                          Category: {task.category}
                        </Typography>
                        <Typography component="span" display="block">
                          Subcategory: {task.subcategory}
                        </Typography>
                        {task.startDate && (
                          <Typography component="span" display="block">                            Start: {formatDateTime(task.startDate)}
                          </Typography>
                        )}
                        {task.endDate && (
                          <Typography component="span" display="block">
                            End: {formatDateTime(task.endDate)}
                          </Typography>
                        )}
                        {task.startDate && (
                          <Typography component="span" display="block" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            Duration: {calculateDuration(task.startDate, task.endDate)}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </Paper>
            ))}
          </List>
        )}

        <Dialog
          open={taskToComplete !== null}
          onClose={handleCancelComplete}
          aria-labelledby="complete-task-dialog"
        >
          <DialogTitle id="complete-task-dialog">Complete Task</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to complete this task? The current date and time will be set as the end date.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelComplete} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmComplete} color="primary" variant="contained">
              Complete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={taskToDelete !== null}
          onClose={handleCancelDelete}
          aria-labelledby="delete-task-dialog"
        >
          <DialogTitle id="delete-task-dialog">Delete Task</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this task? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} color="primary" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default TaskListView;
